import React from 'react';

import type { ChannelState } from '../../theme/tokens';
import { StatBadge } from '../StatBadge/StatBadge';
import { Reveal } from '../Reveal/Reveal';
import { Skeleton } from '../Skeleton/Skeleton';
import { Root, Label, ValueBox, Value, type MetricSurface } from './MetricCard.styles';

export { MetricGrid } from './MetricCard.styles';
export type { MetricSurface };

export type MetricCardProps = {
  label: string;
  value: string;
  /** Optional delta indicator. All three must be present to render the badge. */
  change?: string;
  changeType?: 'increase' | 'decrease';
  vs?: string;
  /** Dim the card (e.g. a metric with no data yet). */
  muted?: boolean;
  /** `plain` (CPA card) vs `panel` (washed inner fill — the performance grid). */
  surface?: MetricSurface;
  /** Channel-health tint on the outer surface. Defaults to the neutral card. */
  tone?: ChannelState;
  /** Lift the (active) card as a tile for a darker grouping container — uses the
   * plain cardBase surface instead of the page-level featureCard fill, which
   * would otherwise match the container (and go white-on-white in light theme). */
  raised?: boolean;
  /** Colour the delta green when it falls instead of rises (CPA card). */
  invertDelta?: boolean;
  /** The figure held steady: the delta reads neutral instead of taking a direction's colour. */
  flat?: boolean;
  /** Footer slot under the value — e.g. the CPA advisory note in a colored state. */
  note?: React.ReactNode;
  /** While true, the value + delta show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * Single-stat card — a label, a prominent value and an optional delta pill.
 * Backs the channel CPA card (`plain` + a health `tone` + an advisory `note`),
 * the channel "Today's Performance" grid (`panel`) and the affiliates overview.
 * The label is static chrome; the value + delta load in (the placeholder lives
 * inside `Value` so it inherits its font).
 */
export const MetricCard = ({
  label,
  value,
  change,
  changeType,
  vs,
  muted = false,
  surface = 'plain',
  tone = 'active',
  raised = false,
  invertDelta = false,
  flat = false,
  note,
  loading = false,
}: MetricCardProps): React.ReactElement => {
  const hasDelta = !!(change && changeType && vs);
  return (
    <Root $muted={muted} $tone={tone} $raised={raised}>
      <Label text={label} tag="span" />
      <ValueBox $surface={surface}>
        <Value>
          <Reveal loading={loading} placeholder={<Skeleton width={`${Math.max(2, value.length)}ch`} height="0.8em" radius={6} />}>
            {value}
          </Reveal>
        </Value>
        {hasDelta ? (
          <Reveal loading={loading} placeholder={<Skeleton width={56} height={18} radius={7} />}>
            <StatBadge
              change={change}
              changeType={changeType}
              vs={vs}
              positiveWhen={invertDelta ? 'decrease' : 'increase'}
              flat={flat}
            />
          </Reveal>
        ) : null}
      </ValueBox>
      {note ?? null}
    </Root>
  );
};

export default MetricCard;
